import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/common'

export const Header = () => {
  const { isAuthenticated, user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const handleHashNavigation = (hash: string) => {
    if (location.pathname === '/') {
      // If already on landing page, just scroll to the section
      const element = document.querySelector(hash)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      // Navigate to landing page first, then scroll after a short delay
      navigate('/')
      setTimeout(() => {
        const element = document.querySelector(hash)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    }
  }

  const handleDashboardNavigation = (hash: string) => {
    if (location.pathname === '/dashboard') {
      // If already on dashboard, just scroll to the section
      const element = document.querySelector(hash)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      // Navigate to dashboard first, then scroll after a short delay
      navigate('/dashboard')
      setTimeout(() => {
        const element = document.querySelector(hash)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    }
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center px-4 py-4 sm:px-10 lg:px-40">
      <div className="flex w-full max-w-[1200px] items-center justify-between backdrop-blur-md bg-white/80 dark:bg-gray-900/80 border border-slate-200/60 dark:border-gray-700/60 rounded-xl px-6 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-primary">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 45.8096C19.6865 45.8096 15.4698 44.5305 11.8832 42.134C8.29667 39.7376 5.50128 36.3314 3.85056 32.3462C2.19985 28.361 1.76794 23.9758 2.60947 19.7452C3.451 15.5145 5.52816 11.6284 8.57829 8.5783C11.6284 5.52817 15.5145 3.45101 19.7452 2.60948C23.9758 1.76795 28.361 2.19986 32.3462 3.85057C36.3314 5.50129 39.7376 8.29668 42.134 11.8833C44.5305 15.4698 45.8096 19.6865 45.8096 24L24 24L24 45.8096Z" fill="currentColor"></path>
              </svg>
            </div>
            <h2 className="text-lg font-bold leading-tight tracking-tight text-text-main-light dark:text-text-main-dark">Binder Tech</h2>
          </Link>
        </div>
        {isAuthenticated ? (
          <nav className="hidden md:flex items-center gap-6 text-text-sub-light dark:text-text-sub-dark">
            {location.pathname === '/dashboard' || location.pathname === '/profile' ? (
              <>
                <Link
                  to="/"
                  className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
                >
                  Home
                </Link>
                <button
                  onClick={() => handleDashboardNavigation('#subscriptions')}
                  className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
                >
                  Subscriptions
                </button>
                <button
                  onClick={() => handleDashboardNavigation('#services')}
                  className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
                >
                  Services
                </button>
                <button
                  onClick={() => handleDashboardNavigation('#usage')}
                  className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
                >
                  Usage
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/dashboard"
                  className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => handleHashNavigation('#services')}
                  className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
                >
                  Services
                </button>
                <button
                  onClick={() => handleHashNavigation('#features')}
                  className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
                >
                  Features
                </button>
              </>
            )}
          </nav>
        ) : (
          <nav className="hidden md:flex items-center gap-8 text-text-sub-light dark:text-text-sub-dark">
            <button
              onClick={() => handleHashNavigation('#services')}
              className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
            >
              Services
            </button>
            <button
              onClick={() => handleHashNavigation('#features')}
              className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
            >
              Features
            </button>
          </nav>
        )}
        <div className="flex items-center gap-4">
        {isAuthenticated ? (
          <>
            <Link
              to="/profile"
              className="hidden sm:flex items-center gap-2 text-sm text-text-sub-light dark:text-text-sub-dark hover:text-primary transition-colors cursor-pointer group"
              title="View Profile"
            >
              <span className="material-symbols-outlined text-base group-hover:scale-110 transition-transform">
                account_circle
              </span>
              <span>{user?.name || user?.email}</span>
            </Link>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                await logout()
                navigate('/')
              }}
            >
              Logout
            </Button>
          </>
        ) : (
            <>
              <Link
                to="/login"
                className="flex h-10 items-center justify-center rounded-lg bg-transparent px-5 text-sm font-semibold text-text-main-light transition-colors hover:bg-black/5 dark:text-text-main-dark dark:hover:bg-white/5"
              >
                <span className="truncate">Log In</span>
              </Link>
              <a
                href="mailto:contact-us@binder.com
                ?subject=Service%20Subscription%20Request
                &body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20submit%20a%20service%20request.%0D%0A%0D%0AORGANIZATION%20NAME:%0D%0ASERVICE%20REQUESTED:%0D%0AADDITIONAL%20DETAILS:%0D%0A%0D%0AThank%20you."
                className="flex h-10 items-center justify-center rounded-lg bg-primary px-5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-blue-600 hover:shadow-xl hover:shadow-primary/30 active:scale-95"
              >
                <span className="truncate">Register</span>
              </a>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

